"use client";

import type { ReactNode } from "react";
import { useTranslations } from "use-intl";

import { FacetFilterControls } from "@/components/facets/facet-filter-controls";
import { FacetFiltersModal } from "@/components/facets/facet-filters-modal";
import type { FilterOption } from "@/components/ui/filter-select";
import { FilterToolbarControls } from "@/components/ui/filter-toolbar";
import type { SurfaceConfig } from "@/facets/config";
import type { FacetFiltersController } from "@/facets/use-facet-filters";

function hasFacetWidgets(
  surface: SurfaceConfig,
  options: Record<string, FilterOption[]>,
  controller: FacetFiltersController,
): boolean {
  return surface.facets.some(
    (def) => def.key !== "format" && ((options[def.key]?.length ?? 0) > 0 || controller.hasActive),
  );
}

interface FacetFilterBarCompactProps {
  surface: SurfaceConfig;
  options: Record<string, FilterOption[]>;
  controller: FacetFiltersController;
  sortOptions: FilterOption[];
  formatSelector: ReactNode;
  searchField: ReactNode;
  desktopSort: ReactNode;
}

/**
 * Compact facet bar: the format segments share a row with the sort select and
 * search spans its own row. On desktop the secondary facets sit inline in a row
 * below search; on mobile they (plus sort) move into a "Filters" sheet beside
 * search. Search never resizes when the format options change.
 */
export function FacetFilterBarCompact({
  surface,
  options,
  controller,
  sortOptions,
  formatSelector,
  searchField,
  desktopSort,
}: FacetFilterBarCompactProps) {
  const t = useTranslations("facets");
  const showFilters = hasFacetWidgets(surface, options, controller);
  const showFiltersButton = showFilters || sortOptions.length > 0;

  return (
    <div className="mb-5 flex flex-col gap-2 py-3 sm:gap-3">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3 empty:hidden">
        <div className="min-w-0 flex-1 has-[>span:empty:only-child]:hidden">{formatSelector}</div>
        {desktopSort}
      </div>
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <div className="min-w-0 flex-1">{searchField}</div>
        {showFiltersButton && (
          <FacetFiltersModal
            surface={surface}
            options={options}
            controller={controller}
            sortOptions={sortOptions}
            className="sm:hidden"
          />
        )}
      </div>
      {showFilters && (
        <div className="hidden sm:block">
          <FilterToolbarControls
            label={t("filterBy")}
            className="sm:w-full sm:flex-nowrap sm:[&>div]:min-w-0 sm:[&>div]:flex-1 sm:[&>div_button]:w-full"
          >
            <FacetFilterControls surface={surface} options={options} controller={controller} />
          </FilterToolbarControls>
        </div>
      )}
    </div>
  );
}
